import FTPlayerOptions from '../ft-player-options';
import { addEvent } from '../utils/events';
import { HAS_MSE, loadScript } from '../utils/general';
import { isDashSource } from '../utils/media';
import Native from './native';

class DashMedia extends Native {

  player!: DashMedia | null;

  events = null;

  options = {};
  startTime = 0;

  // Keep a single dashjs MediaPlayer (and its negotiated DRM/EME session) alive for the
  // whole app session instead of destroying/recreating it on every midroll/content change,
  // so the CDM doesn't redo license negotiation each time.
  static sharedPlayer: any = null;
  static isProtectionInitialized = false;
  static scriptLoadPromise: Promise<any> | null = null;

  constructor(videoEl: HTMLVideoElement, mediaSource: any, options: FTPlayerOptions, startTime: number) {
    super(videoEl, mediaSource);
    this.options = options;
    this.startTime = startTime;
    // Bound once so on()/off() reference the same function - .bind(this) creates a new
    // reference every call, so re-binding at off() time would never actually unregister it.
    this._boundAssign = this._assign.bind(this);

    function createInstance() {
      this.player = DashMedia.sharedPlayer || (DashMedia.sharedPlayer = dashjs.MediaPlayer().create());
    }
    if (!DashMedia.scriptLoadPromise) {
      DashMedia.scriptLoadPromise = (typeof dashjs === 'undefined') ?
        // Ever-green script
        loadScript(platformSettings.path + 'modules/dashjs/dash.all.min.js') :
        new Promise(resolve => resolve(true));
    }
    this.promise = DashMedia.scriptLoadPromise;

    this.promise.then(createInstance.bind(this));
    return this;
  }

  canPlayType(mimeType) {
    return HAS_MSE && mimeType === 'application/dash+xml';
  }

  load() {
    this._preparePlayer();
    this.player.attachSource(this.media.src);

    // const e = addEvent('loadedmetadata');
    // this.videoEl.dispatchEvent(e);

    if (!this.events) {
      this.events = dashjs.MediaPlayer.events;
      Object.keys(this.events).forEach(event => {
        this.player.on(this.events[event], this._boundAssign);
      });
    }
  }

  destroy(resetFullPlayer) {
    this._revoke(resetFullPlayer);
  }

  set src(media) {
    if (isDashSource(media)) {
      this._revoke();
      this.player = DashMedia.sharedPlayer || (DashMedia.sharedPlayer = dashjs.MediaPlayer().create());
      this._boundAssign = this._assign.bind(this);
      //this._preparePlayer();
      //this.player.attachSource(media.src);

      this.events = dashjs.MediaPlayer.events;
      Object.keys(this.events).forEach(event => {
        this.player.on(this.events[event], this._boundAssign);
      });
    }
  }

  get levels() {
    const levels = [];
    if (this.player) {
      const bitrates = this.player.getBitrateInfoListFor('video');
      if (bitrates.length) {
        bitrates.forEach((item) => {
          if (bitrates[item]) {
            const { height, name } = bitrates[item];
            const level = {
              height,
              id: item,
              label: name || null,
            };
            levels.push(level);
          }
        });
      }
    }
    return levels;
  }

  set level(level) {
    if (level === 0) {
      this.player.setAutoSwitchQuality(true);
    } else {
      this.player.setAutoSwitchQuality(false);
      this.player.setQualityFor('video', level);
    }
  }

  get level() {
    return this.player ? this.player.getQualityFor('video') : -1;
  }

  _assign(event) {
    if (event.type === 'error') {
      const details = {
        detail: {
          type: 'M(PEG)-DASH',
          message: event,
        },
      };
      const errorEvent = addEvent('playererror', details);
      this.videoEl.dispatchEvent(errorEvent);
    } else {
      //console.log('[ft-streaming-player] : ' + event.type);
      const e = addEvent(event.type, event);
      if (event.type == 'playbackMetaDataLoaded') {
        if (this.startTime > 0) {
          this.videoEl.currentTime = this.startTime;
        }
        //this.videoEl.play()
      }
      this.videoEl && this.videoEl.dispatchEvent(e);
    }
  }

  static _revokeSharedPlayer(videoEl?: HTMLVideoElement | null) {
    const player = DashMedia.sharedPlayer
    // Clear static state first so a throw below can't leave a half-dead player to be reused
    DashMedia.sharedPlayer = null
    DashMedia.isProtectionInitialized = false
    if (player) {
      try {
        player.updateSettings({ streaming: { protection: { keepProtectionMediaKeys: false } } })
        player.reset()
        player.destroy()
      } catch (e) {
        // A suspended CDM can throw here - static state is already cleared above
      }
    }
    // Release keys from the element regardless of player state - covers DRM -> HLS -> suspend,
    // where the element still holds MediaKeys but the DashMedia that attached them is gone.
    if (videoEl && typeof videoEl.setMediaKeys === 'function' && videoEl.mediaKeys) {
      videoEl.setMediaKeys(null).catch(() => { })
    }
  }

  _revoke(resetFullPlayer) {
    if (this.events) {
      Object.keys(this.events).forEach(event => {
        this.player.off(this.events[event], this._boundAssign);
      });
      this.events = null;
    }
    if (this.player) {
      // reset() drops the current source/stream but - with keepProtectionMediaKeys:true -
      // preserves the negotiated MediaKeys/session, so the shared player can be reused by
      // the next DashMedia instance without renegotiating DRM. No destroy(): that would
      // tear down the protection controller/EME session entirely.
      //this.player.reset()
      // On a full reset (app suspend) the shared player is torn down by Media.destroy() via
      // DashMedia._revokeSharedPlayer(videoEl), which also releases MediaKeys from the element.
      if (!resetFullPlayer) {
        this.player.pause()
        this.player.attachView(null)
      }
      this.player = null
    }
    if (this.videoEl) {
      this.videoEl = null
    }
  }

  _preparePlayer() {
    // In version 3x, `getDebug` is deprecated
    //if (typeof this.player.getDebug().setLogToBrowserConsole === 'undefined') {
    this.player.updateSettings({
      debug: {
        logLevel: dashjs.Debug.LOG_LEVEL_NONE,
      },
      streaming: {
        buffer: {
          fastSwitchEnabled: true,
          bufferPruningInterval: 10,
          bufferToKeep: 20,
          bufferTimeAtTopQuality: 10,
          bufferTimeAtTopQualityLongForm: 15,
          stableBufferTime: 8,
          minBufferTime: 4,
          initialBufferLevel: 6
        },
        protection: {
          keepProtectionMediaKeys: true // was false - preserves MediaKeys/sessions across reset()
        },
        scheduling: {
          scheduleWhilePaused: false
        }
      },
    });

    if (!DashMedia.isProtectionInitialized) {
      // First-ever setup on the shared player: DRM config + initialize() must only run
      // once per player lifetime - re-running initialize() on an already-initialized
      // player is exactly what this shared-player approach avoids.
      if (this.options && typeof this.options.drm === 'object' && Object.keys(this.options.drm).length) {
        this.player.setProtectionData(this.options.drm);
        if (this.options.robustnessLevel && this.options.robustnessLevel) {
          this.player.getProtectionController().setRobustnessLevel(this.options.robustnessLevel);
        }
      }
      this.player.initialize(this.videoEl, this.media.src, true);
      DashMedia.isProtectionInitialized = true;
    } else {
      // Reusing the shared player/session - just rebind the (possibly new) video element;
      // load() will attachSource() the new manifest right after this returns.
      this.player.attachView(this.videoEl);

      // 2. Load a fresh stream if needed, otherwise it retains old stream data pointer
      this.player.attachSource(this.media.src);

      // 3. Fire playback safely
      this.player.play();

    }
  }
}

export default DashMedia;
